import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import { setSentryUser, clearSentryUser } from '../../lib/sentry';

const capitalize = (input: string): string => {
  if (!input) return ''
  return input.charAt(0).toUpperCase() + input.slice(1)
}

const ensureTwoWordDisplayName = (value: string | null | undefined, firstName?: string | null): string => {
  const trimmed = (value ?? '').trim().replace(/\s+/g, ' ')
  if (!trimmed) {
    const base = capitalize((firstName ?? '').trim()) || 'New'
    return `${base} Rider`
  }

  const parts = trimmed.split(' ')
  if (parts.length >= 2) {
    return trimmed
  }

  return `${trimmed} Routes`
}

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role?: string;
}

// Sign in with email and password
export const signInWithEmail = async (email: string, password: string): Promise<AuthUser> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return mapFirebaseUser(userCredential.user);
};

// Sign up with email and password
export const signUpWithEmail = async (
  email: string, 
  password: string, 
  displayName: string,
  additionalData?: {
    firstName?: string;
    lastName?: string;
    username?: string;
    displayName?: string;
  }
): Promise<AuthUser> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const normalizedDisplayName = ensureTwoWordDisplayName(displayName, additionalData?.firstName);
  
  // Update the user's display name
  await updateProfile(userCredential.user, { displayName: normalizedDisplayName });
  
  // Create user profile in Firestore with additional data
  await createUserProfile(userCredential.user, {
    ...additionalData,
    displayName: normalizedDisplayName
  });
  
  return mapFirebaseUser(userCredential.user);
};

// Sign in with Google
export const signInWithGoogle = async (): Promise<AuthUser> => {
  const provider = new GoogleAuthProvider();
  const userCredential = await signInWithPopup(auth, provider);
  
  // Create/update user profile in Firestore
  await createUserProfile(userCredential.user);
  
  return mapFirebaseUser(userCredential.user);
};

// Sign out
export const signOutUser = async (): Promise<void> => {
  await signOut(auth);
  clearSentryUser();
};

// Reset password
export const resetPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

// Create or update user profile in Firestore
const createUserProfile = async (
  user: User,
  additionalData?: {
    firstName?: string;
    lastName?: string;
    username?: string;
    displayName?: string;
  }
): Promise<void> => {
  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    // Extract name parts from display name if not provided
    const normalizedDisplayName = ensureTwoWordDisplayName(additionalData?.displayName ?? user.displayName, additionalData?.firstName)
    const nameParts = normalizedDisplayName.split(' ');
    const firstName = (additionalData?.firstName || nameParts[0] || '').trim();
    const lastName = (additionalData?.lastName || nameParts.slice(1).join(' ') || '').trim();
    const username = additionalData?.username || user.email?.split('@')[0] || '';
    
    // Create new user document
    await setDoc(userRef, {
      displayName: normalizedDisplayName,
      email: user.email || '',
      photoURL: user.photoURL || '',
      firstName,
      lastName,
      username,
      role: 'user', // Default role
      createdAt: new Date(),
      updatedAt: new Date(),
      profile: {
        bio: '',
        location: '',
        website: '',
        socialLinks: {}
      },
      preferences: {
        emailNotifications: true,
        profileVisibility: 'public'
      },
      stats: {
        totalRoutes: 0,
        publishedRoutes: 0,
        premiumRoutes: 0,
        totalDistance: 0,
        clubMemberships: 0,
        followedClubs: 0
      }
    });
  } else {
    // Update existing user's last login
    await updateDoc(userRef, {
      lastLogin: new Date(),
      photoURL: user.photoURL || userDoc.data().photoURL
    });
  }
};

// Map Firebase User to our AuthUser interface
const mapFirebaseUser = (user: User | null): AuthUser | null => {
  if (!user) return null;
  
  const authUser = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    role: undefined // Will be populated from Firestore
  };
  
  // Set user context in Sentry
  setSentryUser({ id: user.uid, email: user.email || undefined });
  
  return authUser;
};

// Update user profile
export const updateUserProfile = async (
  userId: string,
  updates: Partial<{
    displayName: string;
    firstName: string;
    lastName: string;
    username: string;
    bio: string;
    location: {
      city?: string;
      country?: string;
      region?: string;
      state?: string;
    };
    city?: string;
    country?: string;
    website: string;
    socialLinks: any;
  }>
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  
  const updateData: any = {
    ...updates,
    updatedAt: new Date()
  };
  
  // If displayName is updated, also update firstName and lastName
  if (updates.displayName) {
    const normalizedDisplayName = ensureTwoWordDisplayName(updates.displayName, updates.firstName)
    const nameParts = normalizedDisplayName.split(' ');
    updateData.displayName = normalizedDisplayName;
    updateData.firstName = updates.firstName?.trim() || nameParts[0] || '';
    updateData.lastName = nameParts.slice(1).join(' ').trim();
  } else if (updates.firstName) {
    updateData.firstName = updates.firstName.trim();
  }
  
  // If city or country are provided separately, also update them
  if (updates.city !== undefined) updateData.city = updates.city;
  if (updates.country !== undefined) updateData.country = updates.country;
  
  await updateDoc(userRef, updateData);
};

// Get user profile from Firestore
export const getUserProfile = async (userId: string): Promise<any> => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
};

// Auth state observer
export const onAuthStateChange = (callback: (user: AuthUser | null) => void) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Get additional user data from Firestore
      const profile = await getUserProfile(user.uid);
      callback({
        ...mapFirebaseUser(user),
        role: profile?.role || 'user'
      });
    } else {
      callback(null);
    }
  });
};

// Force refresh the user's ID token to get updated custom claims
export const refreshUserToken = async (): Promise<void> => {
  const user = auth.currentUser;
  if (user) {
    await user.getIdToken(true); // Force refresh
    // Removed sensitive logging
  }
};

// Get current user's custom claims
export const getUserClaims = async (): Promise<any> => {
  const user = auth.currentUser;
  if (user) {
    const idTokenResult = await user.getIdTokenResult();
    return idTokenResult.claims;
  }
  return null;
};
